import type { ReactNode } from 'react';
import { Star } from 'lucide-react';
import { NodeThumb } from '@/components/value-chain/explorer/NodeThumb';
import {
  companyInitials,
  costDots,
  evidenceLabel,
  importanceFromRank,
  shortName,
} from '@/components/value-chain/nodeVisuals';
import type { TreeIndex } from '@/components/value-chain/treeIndex';
import { childrenOf, companiesOf } from '@/components/value-chain/treeIndex';
import type { ValueChainInspectorTab, ValueChainNode } from '@/types/valueChain';

const TABS: Array<{ id: ValueChainInspectorTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'supply', label: 'Supply Chain' },
  { id: 'manufacturing', label: 'Manufacturing' },
  { id: 'materials', label: 'Materials' },
  { id: 'financials', label: 'Financials' },
];

type Props = {
  node: ValueChainNode;
  index: TreeIndex;
  tab: ValueChainInspectorTab;
  onTabChange: (tab: ValueChainInspectorTab) => void;
  starred: boolean;
  onToggleStar: () => void;
  usedIn: string;
};

export function NodeInspector({
  node,
  index,
  tab,
  onTabChange,
  starred,
  onToggleStar,
  usedIn,
}: Props) {
  const inbound = index.inboundBom.get(node.id);
  const rank = inbound?.rank ?? null;
  const importance = importanceFromRank(rank);
  const suppliers = uniqueSuppliers(node, index);
  const children = childrenOf(index, node.id);
  const materials = children.filter(
    (child) => child.type === 'Material' || child.type === 'Chemical / Dopant',
  );
  const manufacturer = node.manufacturer || suppliers[0]?.name || 'Not disclosed';
  const dots = costDots(rank);
  const functionText = inbound?.function || node.description;

  return (
    <section className="flex min-h-0 min-w-[300px] flex-1 flex-col rounded-2xl border border-gray-200 bg-white">
      <div className="flex items-start gap-3 px-4 pt-4 pb-2">
        <NodeThumb node={node} size="lg" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[18px] leading-tight font-semibold text-gray-900">
            {shortName(node.name)}
          </h2>
          <p className="mt-0.5 text-[12px] text-gray-400">{node.type}</p>
        </div>
        <button
          type="button"
          onClick={onToggleStar}
          className={`rounded-lg p-1.5 ${starred ? 'text-amber-500' : 'text-gray-300 hover:text-gray-500'}`}
          aria-label="Save node"
        >
          <Star className={`h-4 w-4 ${starred ? 'fill-current' : ''}`} />
        </button>
      </div>

      <div className="flex gap-3 overflow-x-auto border-b border-gray-200 px-4">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onTabChange(item.id)}
            className={`relative shrink-0 py-2.5 text-[13px] ${
              tab === item.id ? 'font-medium text-blue-700' : 'text-gray-400 hover:text-gray-700'
            }`}
          >
            {item.label}
            {tab === item.id ? (
              <span className="absolute inset-x-0 -bottom-px h-0.5 bg-blue-600" />
            ) : null}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {tab === 'overview' ? (
          <>
            <h3 className="mb-3 text-[13px] font-semibold text-gray-900">Key Information</h3>
            <dl className="space-y-2.5 text-[13px]">
              <Row label="Function" value={functionText} />
              <Row label="Used in" value={usedIn} />
              <Row label="Supplier / Manufacturer" value={manufacturer} />
              <Row
                label="Estimated importance"
                value={importance ?? 'Not ranked'}
                extra={
                  importance ? (
                    <span className="ml-2 inline-flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          className={`h-2 w-2 rounded-full ${i < dots ? 'bg-gray-900' : 'bg-gray-200'}`}
                        />
                      ))}
                    </span>
                  ) : null
                }
              />
              <Row
                label="Estimated cost contribution"
                value={
                  dots
                    ? ''
                    : 'Not quantified'
                }
                extra={
                  dots ? (
                    <span className="inline-flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span
                          key={i}
                          className={`h-2.5 w-2.5 rounded-full ${i < dots ? 'bg-gray-900' : 'bg-gray-200'}`}
                        />
                      ))}
                    </span>
                  ) : null
                }
              />
              <Row
                label="Confidence"
                value={confidenceCopy(node.evidence_status, suppliers.length)}
              />
            </dl>

            {node.notes || node.description ? (
              <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50 px-3 py-3">
                <p className="mb-1 text-[12px] font-semibold text-amber-900">Why it matters</p>
                <p className="text-[12px] leading-relaxed text-amber-950/80">
                  {firstSentences(node.notes || node.description, 2)}
                </p>
              </div>
            ) : null}

            <h3 className="mt-5 mb-3 text-[13px] font-semibold text-gray-900">
              Key suppliers (direct and inferred)
            </h3>
            {suppliers.length === 0 ? (
              <p className="text-[13px] text-gray-400">No mapped supplier for this node.</p>
            ) : (
              <div className="space-y-2">
                {suppliers.slice(0, 6).map((supplier) => (
                  <div key={supplier.id} className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-[10px] font-semibold text-gray-700">
                      {companyInitials(supplier.name)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium text-gray-900">
                        {shortName(supplier.name)}
                      </span>
                      <span className="block truncate text-[11px] text-gray-400">
                        {supplier.description}
                      </span>
                    </span>
                    <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                      {evidenceLabel(node.evidence_status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : null}

        {tab === 'supply' ? (
          <RelationList
            empty="No supply-chain links on this node."
            rows={suppliers.map((supplier) => ({
              id: supplier.id,
              title: supplier.name,
              detail: supplier.description,
            }))}
          />
        ) : null}

        {tab === 'manufacturing' ? (
          <RelationList
            empty="No manufacturing notes."
            rows={[
              {
                id: 'mfg',
                title: manufacturer,
                detail: node.notes || 'Public manufacturing source is limited to the mapped supplier.',
              },
            ]}
          />
        ) : null}

        {tab === 'materials' ? (
          <RelationList
            empty="No first-order materials mapped under this node."
            rows={materials.map((material) => ({
              id: material.id,
              title: shortName(material.name),
              detail: material.description,
            }))}
          />
        ) : null}

        {tab === 'financials' ? (
          <div className="text-[13px] leading-relaxed text-gray-600">
            <p>
              Rank {rank ?? 'n/a'} of this parent&apos;s bill of materials. Dots estimate relative
              cost importance from hop ranking, not a quoted dollar BOM.
            </p>
            {node.notes ? <p className="mt-3">{node.notes}</p> : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Row({
  label,
  value,
  extra,
}: {
  label: string;
  value: string;
  extra?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[148px_1fr] items-start gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900">
        <span>{value}</span>
        {extra}
      </dd>
    </div>
  );
}

function RelationList({
  rows,
  empty,
}: {
  rows: Array<{ id: string; title: string; detail: string }>;
  empty: string;
}) {
  if (rows.length === 0) return <p className="text-[13px] text-gray-400">{empty}</p>;
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
          <p className="text-[13px] font-medium text-gray-900">{row.title}</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-gray-500">{row.detail}</p>
        </div>
      ))}
    </div>
  );
}

function uniqueSuppliers(node: ValueChainNode, index: TreeIndex): ValueChainNode[] {
  const direct = companiesOf(index, node.id);
  if (direct.length) return direct;
  if (!node.manufacturer) return [];
  return [
    {
      id: `named:${node.manufacturer}`,
      name: node.manufacturer,
      type: 'Company',
      description: node.notes || 'Named on this hop',
    },
  ];
}

function confidenceCopy(status?: string, supplierCount = 0): string {
  if (status === 'confirmed' && supplierCount > 1) {
    return 'High for inclusion, medium for supplier split';
  }
  if (status === 'confirmed') return 'High for inclusion';
  if (status === 'inferred') return 'Medium — inferred from public evidence';
  return 'Low — supplier or identity not confirmed';
}

function firstSentences(text: string, count: number): string {
  const parts = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  return parts.slice(0, count).join(' ');
}
